import { NAP } from '../config/nap';
import { urlLang } from '../i18n';
import { normaliseProject } from '../config/projectSeo';
import { projectFaq } from '../config/projectContent';
import { localizedPath } from '../config/langs';
import {
    ORG_ID,
    WEBSITE_ID,
    orgRef,
    organizationNode,
    websiteNode,
    breadcrumbNode,
    faqNode,
    projectNode,
    realEstateListingNode,
    articleNode
} from '../config/schema';

const SITE_URL = NAP.url;

/**
 * Injects a JSON-LD structured-data block into <head>.
 * React 19 hoists <script> children with type application/ld+json.
 *
 * Every builder below is a thin wrapper over src/config/schema.js — the same
 * module the prerenderer uses — so the graph in the static HTML and in the
 * hydrated page is one and the same. `lang` defaults to the URL's language,
 * which also decides the prefix on every URL in the graph.
 */
export function JsonLd({ data }) {
    if (!data) return null;
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
    );
}

export { ORG_ID, WEBSITE_ID, orgRef, SITE_URL };

export const organizationSchema = (lang = urlLang) => organizationNode(lang);
export const websiteSchema = (lang = urlLang) => websiteNode(lang);

/** items: [{ name, url }] with unprefixed paths, as the pages pass them. */
export const breadcrumbSchema = (items, lang = urlLang) =>
    breadcrumbNode(items.map((it) => ({ name: it.name, path: it.path ?? it.url })), lang);

export const projectSchema = (project, lang = urlLang) => (project && normaliseProject(project).id ? projectNode(project, lang) : null);
export const realEstateListingSchema = (project, lang = urlLang) => (project ? realEstateListingNode(project, lang) : null);

export function articleSchema({ title, description, image, slug, publishedAt, updatedAt, lang = urlLang }) {
    return articleNode({ title, description, image, path: `/blog/${slug}`, publishedAt, updatedAt, lang });
}

/** url: the absolute page URL, or omit it for an inline FAQ. */
export const faqSchema = (items, { url, lang = urlLang } = {}) => faqNode(items, url, lang);

/** FAQ block for a project page, built from the shared copy module. */
export function projectFaqSchema(project, lang = urlLang) {
    const p = normaliseProject(project);
    if (!p.id || !p.title) return null;
    return faqNode(projectFaq(project, lang), `${SITE_URL}${localizedPath(`/project/${p.id}`, lang)}`, lang);
}
